import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import MyPageLayout from '../../components/mypage/MyPageLayout';
import MyPageBreadcrumb from '../../components/mypage/MyPageBreadcrumb';
import { useUserInfoSimple } from '../../hooks/useUserInfoSimple';
import { uploadProfileImage, updateBasicInfo, changePassword } from '../../services/auth/authApi';
import { updateMarketingConsent } from '../../services/mypageApi';
import { apiClient } from '../../services';
import BasicInfoEditModal from '../../components/modals/BasicInfoEditModal';
import defaultProfileImage from '../../assets/images/profile.png';
import { getUniversities, getDepartments } from '../../services/personApi';

interface MyPageProfileEditProps {}

// 신분 인증 요청 타입 (API 스펙에 맞춘)
interface IdentityVerificationRequest {
  identity: 'STUDENT' | 'DOCTOR';
  department: string;
  idCardFile?: File;
  licenseNumber?: string;
}

// 신분 인증 응답 타입
interface IdentityVerificationResponse {
  code: number;
  status: string;
  message: string;
  data: {
    identity: string;
    department: string;
    id_card_file?: string;
    license_number?: string;
  };
}

// 마케팅 동의 응답 타입
interface MarketingConsentResponse {
  code: number;
  status: string;
  message: string;
  data: {};
}

// 신분 인증 API 호출 함수
const verifyIdentity = async (data: IdentityVerificationRequest): Promise<IdentityVerificationResponse> => {
  const formData = new FormData();
  formData.append('identity', data.identity);
  formData.append('department', data.department);
  
  if (data.idCardFile) {
    formData.append('idCardFile', data.idCardFile);
  }
  
  if (data.licenseNumber) {
    formData.append('licenseNumber', data.licenseNumber);
  }

  const response = await apiClient.put<IdentityVerificationResponse>('/api/persons/identity', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    withCredentials: true,
  });
  
  if (response.data.code !== 1073741824) { // API 성공 코드 기준
    throw new Error(response.data.message || '신분 인증에 실패했습니다.');
  }
  
  return response.data;
};

const MyPageProfileEdit: React.FC<MyPageProfileEditProps> = () => {
  const { userInfo, loading, refresh } = useUserInfoSimple();
  const [isEditingBasicInfo, setIsEditingBasicInfo] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [activeVerificationTab, setActiveVerificationTab] = useState<'student' | 'doctor'>('doctor');
  const [marketingConsent, setMarketingConsent] = useState(false); // userInfo에서 가져올 예정
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [isBasicInfoModalOpen, setIsBasicInfoModalOpen] = useState(false);
  
  // API에서 가져온 학교/진료과 목록 state
  const [universities, setUniversities] = useState<string[]>([]);
  const [departments, setDepartments] = useState<string[]>([]);
  const [isLoadingOptions, setIsLoadingOptions] = useState({
    universities: false,
    departments: false
  });
  
  // 파일 입력 참조
  const fileInputRef = useRef<HTMLInputElement>(null);
  const identityFileInputRef = useRef<HTMLInputElement>(null);
  
  // 회원 자격 인증 상태
  const [isSubmittingIdentity, setIsSubmittingIdentity] = useState(false);
  const [identityFormData, setIdentityFormData] = useState({
    department: '',
    licenseNumber: '',
    selectedFile: null as File | null
  });

  // userInfo가 변경될 때 identityFormData 초기화
  useEffect(() => {
    if (userInfo) {
      setIdentityFormData(prev => ({
        ...prev,
        // 기본 옵션을 선택하도록 빈 문자열로 설정
        department: '',
        licenseNumber: userInfo.license || ''
      }));
    }
  }, [userInfo]);

  // 컴포넌트 마운트 시 사용자 정보 로드
  useEffect(() => {
    const token = localStorage.getItem('access_token');
    
    if (token && !userInfo && !loading) {
      refresh();
    }
  }, []); // 빈 의존성 배열로 마운트 시에만 실행

  // API에서 학교/진료과 목록 불러오기
  useEffect(() => {
    const loadOptions = async () => {
      try {
        // 학교 목록 로드
        setIsLoadingOptions(prev => ({ ...prev, universities: true }));
        const universitiesData = await getUniversities();
        setUniversities(universitiesData);

        // 진료과 목록 로드  
        setIsLoadingOptions(prev => ({ ...prev, departments: true }));
        const departmentsData = await getDepartments();
        setDepartments(departmentsData);
      } catch (error) {
        /// console.error('학교/진료과 목록 로드 실패:', error);
        // 에러가 발생해도 빈 배열로 설정하여 UI 오류 방지
        setUniversities([]);
        setDepartments([]);
      } finally {
        setIsLoadingOptions({ universities: false, departments: false });
      }
    };

    loadOptions();
  }, []); // 컴포넌트 마운트 시 한 번만 실행

  // 생년월일 드롭다운 상태
  const [dropdownStates, setDropdownStates] = useState({
    year: false,
    month: false,
    day: false
  });
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    birth: '',
    birthYear: '',
    birthMonth: '',
    birthDay: '',
    identity: 'DOCTOR',
    university: '',
    medicine: '',
    introduction: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
    specialization: '',
    licenseNumber: ''
  });

  // 사용자 정보 로드 및 폼 데이터 설정
  useEffect(() => {
    if (userInfo) {
      setMarketingConsent(userInfo.marketing_agreed || false);
      
      const birthParts = userInfo.birth ? userInfo.birth.split('-') : ['2000', '01', '01'];
      const [birthYear = '2000', birthMonth = '01', birthDay = '01'] = birthParts;
      
      const formattedPhone = userInfo.phone && userInfo.phone.length === 11 
        ? `${userInfo.phone.slice(0, 3)}-${userInfo.phone.slice(3, 7)}-${userInfo.phone.slice(7)}`
        : userInfo.phone || '010-0000-0000';
      
      setFormData(prev => ({
        ...prev,
        name: userInfo.name || '',
        email: userInfo.email || '',
        phone: formattedPhone,
        birth: userInfo.birth || '',
        birthYear,
        birthMonth,
        birthDay,
        identity: userInfo.identity || 'DOCTOR',
        university: userInfo.university || '',
        medicine: userInfo.medicine || '',
        introduction: userInfo.introduction || '',
        specialization: userInfo.medicine || '',
      }));
      
      if (userInfo.identity === 'STUDENT') {
        setActiveVerificationTab('student');
      } else {
        setActiveVerificationTab('doctor');
      }
    }
  }, [userInfo]); // userInfo만 의존성으로 추가

  // 프로필 이미지 업로드 함수
  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    /// console.log('handleImageUpload 시작');
    
    const file = event.target.files?.[0];
    if (!file) {
      /// console.log('파일이 선택되지 않음');
      return;
    }

    // console.log('선택된 파일:', {
    //   name: file.name,
    //   type: file.type,
    //   size: file.size
    // });

    // 파일 타입 검증
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      /// console.log('지원하지 않는 파일 형식:', file.type);
      alert('JPG, PNG, GIF, WebP 형식의 이미지만 업로드 가능합니다.');
      return;
    }

    // 파일 크기 검증 (5MB)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      /// console.log('파일 크기 초과:', file.size);
      alert('이미지 크기는 5MB 이하만 업로드 가능합니다.');
      return;
    }

    setIsUploadingImage(true);
    try {
      /// console.log('프로필 이미지 업로드 시작:', file.name);
      
      // uploadProfileImage 함수 호출
      const updatedUserInfo = await uploadProfileImage(file);
      /// console.log('프로필 이미지 업로드 성공:', updatedUserInfo);
      
      // 사용자 정보 새로고침
      /// console.log('사용자 정보 새로고침 시작');
      await refresh();
      /// console.log('사용자 정보 새로고침 완료');
      
      alert('프로필 이미지가 변경되었습니다.');
    } catch (error) {
      /// console.error('프로필 이미지 업로드 실패 - 전체 에러:', error);
      /// console.error('에러 메시지:', error instanceof Error ? error.message : '알 수 없는 에러');
      /// console.error('에러 스택:', error instanceof Error ? error.stack : 'No stack');
      
      const errorMessage = error instanceof Error ? error.message : '프로필 이미지 업로드에 실패했습니다.';
      alert(`업로드 실패: ${errorMessage}`);
    } finally {
      /// console.log('업로드 프로세스 완료, 로딩 해제');
      setIsUploadingImage(false);
      // 파일 입력 초기화
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // 기본정보 수정 완료 핸들러
  const handleBasicInfoUpdate = async (data: { name: string; birth: string; phone: string }) => {
    try {
      /// console.log('기본정보 수정 시작:', data);
      
      // 폰 번호에서 하이픈 제거
      const phoneNumber = data.phone.replace(/-/g, '');
      
      const updateData = {
        name: data.name,
        birth: data.birth,
        phone: phoneNumber
      };
      
      await updateBasicInfo(updateData);
      /// console.log('기본정보 수정 성공');
      
      // 사용자 정보 새로고침
      await refresh();
      
      // 모달 닫기
      setIsBasicInfoModalOpen(false);
      
      alert('기본 정보가 수정되었습니다.');
    } catch (error) {
      /// console.error('기본정보 수정 실패:', error);
      alert(error instanceof Error ? error.message : '기본정보 수정에 실패했습니다.');
    }
  };

  // 프로필 이미지 클릭 핸들러
  const handleProfileImageClick = () => {
    if (isUploadingImage) return;
    fileInputRef.current?.click();
  };

  // 마케팅 수신 동의 토글 핸들러
  const handleMarketingConsentToggle = async () => {
    const newConsentStatus = !marketingConsent;
    
    try {
      // console.log('마케팅 수신 동의 상태 변경 시작:', {
      //   current: marketingConsent,
      //   new: newConsentStatus
      // });
      
      // API 호출
      await updateMarketingConsent(newConsentStatus);
      
      // 상태 업데이트
      setMarketingConsent(newConsentStatus);
      
      // 사용자 정보 새로고침
      await refresh();
      
      /// console.log('마케팅 수신 동의 상태 변경 완료');
      alert(`마케팅 수신 동의가 ${newConsentStatus ? '활성화' : '비활성화'}되었습니다.`);
      
    } catch (error) {
      /// console.error('마케팅 수신 동의 상태 변경 실패:', error);
      alert(error instanceof Error ? error.message : '마케팅 수신 동의 상태 변경에 실패했습니다.');
    }
  };

  // 회원 자격 인증 폼 입력 핸들러
  const handleIdentityInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setIdentityFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // 회원 자격 인증 파일 업로드 핸들러
  const handleIdentityFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // 파일 타입 및 크기 검증
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        alert('이미지 파일(JPG, PNG, GIF, WebP) 또는 PDF 파일만 업로드 가능합니다.');
        return;
      }
      
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        alert('파일 크기는 10MB 이하만 업로드 가능합니다.');
        return;
      }
      
      setIdentityFormData(prev => ({
        ...prev,
        selectedFile: file
      }));
    }
  };

  // 회원 자격 인증 제출 핸들러
  const handleIdentitySubmit = async () => {
    try {
      setIsSubmittingIdentity(true);
      
      // 입력값 검증
      if (!identityFormData.department) {
        alert(activeVerificationTab === 'student' ? '소속 대학을 선택해주세요.' : '진료과를 선택해주세요.');
        return;
      }
      
      if (activeVerificationTab === 'student' && !identityFormData.selectedFile) {
        alert('학생증 또는 재학증명서 파일을 업로드해주세요.');
        return;
      }
      
      if (activeVerificationTab === 'doctor' && !identityFormData.licenseNumber) {
        alert('면허번호를 입력해주세요.');
        return;
      }
      
      // API 요청 데이터 구성
      const requestData: IdentityVerificationRequest = {
        identity: activeVerificationTab === 'student' ? 'STUDENT' : 'DOCTOR',
        department: identityFormData.department,
      };
      
      if (activeVerificationTab === 'student' && identityFormData.selectedFile) {
        requestData.idCardFile = identityFormData.selectedFile;
      }
      
      if (activeVerificationTab === 'doctor' && identityFormData.licenseNumber) {
        requestData.licenseNumber = identityFormData.licenseNumber;
      }
      
      /// console.log('회원 자격 인증 요청 데이터:', requestData);
      
      // API 호출
      const response = await verifyIdentity(requestData);
      
      /// console.log('회원 자격 인증 성공:', response);
      
      // 성공 메시지
      alert('회원 자격 인증 요청이 제출되었습니다. 관리자 확인 후 승인이 완료됩니다.');
      
      // 폼 초기화
      setIdentityFormData({
        department: '',
        licenseNumber: '',
        selectedFile: null
      });
      
      // 파일 입력 초기화
      if (identityFileInputRef.current) {
        identityFileInputRef.current.value = '';
      }
      
      // 사용자 정보 새로고침
      await refresh();
      
    } catch (error) {
      /// console.error('회원 자격 인증 실패:', error);
      alert(error instanceof Error ? error.message : '회원 자격 인증에 실패했습니다.');
    } finally {
      setIsSubmittingIdentity(false);
    }
  };

  // 회원 자격 인증 탭 변경 핸들러
  const handleVerificationTabChange = (tab: 'student' | 'doctor') => {
    setActiveVerificationTab(tab);
    
    // 탭 변경 시 기본 옵션을 선택하도록 초기화
    setIdentityFormData(prev => ({
      ...prev,
      department: '', // 기본 옵션을 선택하도록 빈 문자열로 설정
      licenseNumber: tab === 'doctor' ? (userInfo?.license || '') : '',
      // 파일은 초기화
      selectedFile: null
    }));
    
    // 파일 입력만 초기화
    if (identityFileInputRef.current) {
      identityFileInputRef.current.value = '';
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // 비밀번호 변경 핸들러
  const handlePasswordChange = async () => {
    // 유효성 검사
    if (!formData.currentPassword) {
      alert('현재 비밀번호를 입력해주세요.');
      return;
    }

    if (!formData.newPassword) {
      alert('새 비밀번호를 입력해주세요.');
      return;
    }

    // 비밀번호 형식 검증 (영문 대소문자, 숫자 조합, 8-16자)
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[a-zA-Z\d@$!%*?&~#^_+=<>{}[\]|\\:";',./`-]{8,16}$/;
    if (!passwordRegex.test(formData.newPassword)) {
      alert('비밀번호는 영문 대소문자, 숫자를 조합하여 8자 이상 16자 이하로 입력해주세요.');
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      alert('새 비밀번호와 비밀번호 확인이 일치하지 않습니다.');
      return;
    }

    try {
      await changePassword(formData.email, formData.newPassword);
      alert('비밀번호가 성공적으로 변경되었습니다.');
      
      // 폼 초기화 및 닫기
      setFormData(prev => ({
        ...prev,
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      }));
      setIsChangingPassword(false);
    } catch (error: any) {
      alert(error.message || '비밀번호 변경에 실패했습니다.');
    }
  };

  // 드롭다운 관련 함수들
  const handleDropdownToggle = (dropdown: keyof typeof dropdownStates) => {
    if (!isEditingBasicInfo) return;
    setDropdownStates(prev => ({
      ...prev,
      [dropdown]: !prev[dropdown]
    }));
  };

  const handleDropdownSelect = (dropdown: keyof typeof dropdownStates, value: string) => {
    if (dropdown === 'year') {
      setFormData(prev => ({ ...prev, birthYear: value }));
    } else if (dropdown === 'month') {
      setFormData(prev => ({ ...prev, birthMonth: value }));
    } else if (dropdown === 'day') {
      setFormData(prev => ({ ...prev, birthDay: value }));
    }
    
    setDropdownStates(prev => ({ ...prev, [dropdown]: false }));
  };

  // 생년월일 옵션 생성
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 100 }, (_, i) => currentYear - i);
  const months = Array.from({ length: 12 }, (_, i) => i + 1);
  const days = Array.from({ length: 31 }, (_, i) => i + 1);

  if (loading) {
    return (
      <MyPageLayout>
        <Container>
          <div style={{ padding: '60px 0' }}>
            로딩 중...
          </div>
        </Container>
      </MyPageLayout>
    );
  }



  return (
    <MyPageLayout>
      <Container>
        <MainContainer>
          {/* 페이지 헤더 */}
          <PageHeader>
            <PageTitle>회원 정보 수정</PageTitle>
          </PageHeader>

          {/* 브레드크럼 */}
          <NavigationContainer>
            <MyPageBreadcrumb 
              items={[
                { label: '홈' },
                { label: '마이페이지' },
                { label: '회원 정보 수정' }
              ]}
            />
          </NavigationContainer>

          {/* 메인 콘텐츠 */}
          <MainContent>
            {/* 계정 정보 섹션 */}
            <Section>
              <SectionHeader>
                <SectionTitle>계정 정보</SectionTitle>
              </SectionHeader>

              {/* 프로필 이미지 */}
              <ProfileImageSection>
                <ProfileImageContainer>
                  <ProfileImageWrapper>
                    {loading || isUploadingImage ? (
                      <SkeletonCircle />
                    ) : (
                      <ProfileImage 
                        src={userInfo?.image_url || defaultProfileImage} 
                        alt="프로필" 
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.src = defaultProfileImage;
                        }}
                      />
                    )}
                    <ProfileImageOverlay className="overlay">
                      {/* 호버 시 표시되는 오버레이 */}
                    </ProfileImageOverlay>
                  </ProfileImageWrapper>
                  
                  {/* 편집 버튼 - 프로필 이미지 밖으로 완전히 분리 */}
                  <EditIconButton onClick={handleProfileImageClick} disabled={isUploadingImage}>
                    {isUploadingImage ? (
                      <UploadSpinner />
                    ) : (
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path fillRule="evenodd" clipRule="evenodd" d="M10.6588 2.54719C11.43 1.77594 12.6805 1.77594 13.4517 2.54719C14.223 3.31845 14.223 4.56891 13.4517 5.34017L5.0351 13.7568C4.92258 13.8694 4.76997 13.9326 4.61084 13.9326H2.6664C2.33503 13.9326 2.06641 13.6639 2.06641 13.3326V11.3881C2.06641 11.229 2.12962 11.0764 2.24214 10.9639L10.6588 2.54719ZM12.6032 3.39572C12.3006 3.09309 11.8099 3.09309 11.5073 3.39572L3.2664 11.6367V12.7326H4.36231L12.6032 4.49164C12.9058 4.18901 12.9058 3.69835 12.6032 3.39572Z" fill="white"/>
                      </svg>
                    )}
                  </EditIconButton>
                </ProfileImageContainer>
                
                {/* 숨겨진 파일 입력 */}
                <HiddenFileInput
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  onChange={handleImageUpload}
                />
              </ProfileImageSection>

              {/* 이메일 */}
              <FormGroup>
                <FormLabel>이메일</FormLabel>
                {loading ? (
                  <SkeletonInput />
                ) : (
                  <DisabledInput value={formData.email} readOnly />
                )}
              </FormGroup>

              {/* 비밀번호 */}
              <FormRow>
                <FormGroup style={{ flex: 1 }}>
                  <FormLabel>비밀번호</FormLabel>
                  {loading ? (
                    <SkeletonInput />
                  ) : (
                    <DisabledInput value="********" readOnly />
                  )}
                </FormGroup>
                {loading ? (
                  <SkeletonButton />
                ) : (
                  <ChangeButton onClick={() => setIsChangingPassword(!isChangingPassword)}>
                    변경하기
                  </ChangeButton>
                )}
              </FormRow>

              {/* 비밀번호 변경 폼 */}
              {isChangingPassword && (
                <PasswordChangeSection>
                  <FormGroup>
                    <FormLabel>비밀번호 변경</FormLabel>
                    <FormInput
                      type="password"
                      name="currentPassword"
                      value={formData.currentPassword}
                      onChange={handleInputChange}
                      placeholder="현재 비밀번호를 입력해주세요."
                    />
                  </FormGroup>
                  
                  <FormGroup>
                    <FormInput
                      type="password"
                      name="newPassword"
                      value={formData.newPassword}
                      onChange={handleInputChange}
                      placeholder="새로운 비밀번호를 입력해주세요."
                    />
                  </FormGroup>
                  
                  <FormGroup>
                    <FormInput
                      type="password"
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      placeholder="비밀번호를 다시 입력해주세요."
                    />
                  </FormGroup>
                  
                  <PasswordHint>
                    영문 대소문자, 숫자를 조합하여 8자 이상 16자 이하로 입력해주세요.
                  </PasswordHint>
                  
                  <PasswordChangeButton
                    active={!!(formData.currentPassword && formData.newPassword && formData.confirmPassword)}
                    disabled={!formData.currentPassword || !formData.newPassword || !formData.confirmPassword}
                    onClick={handlePasswordChange}
                  >
                    비밀번호 변경하기
                  </PasswordChangeButton>
                </PasswordChangeSection>
              )}
            </Section>

            <Divider />

            {/* 기본 정보 섹션 */}
            <Section>
              <SectionHeader>
                <SectionTitle>기본 정보</SectionTitle>
                {loading ? (
                  <SkeletonButton />
                ) : (
                  <EditButton 
                    active={false}
                    onClick={() => setIsBasicInfoModalOpen(true)}
                  >
                    수정하기
                  </EditButton>
                )}
              </SectionHeader>

              {/* 이름 */}
              <FormGroup>
                <FormLabel>이름</FormLabel>
                {loading ? (
                  <SkeletonInput />
                ) : isEditingBasicInfo ? (
                  <FormInput
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                  />
                ) : (
                  <DisabledInput value={formData.name} readOnly />
                )}
              </FormGroup>

              {/* 생년월일 */}
              <FormGroup>
                <FormLabel>생년월일</FormLabel>
                <BirthContainer>
                  {loading ? (
                    <>
                      <SkeletonBirthInput />
                      <SkeletonBirthInput />
                      <SkeletonBirthInput />
                    </>
                  ) : isEditingBasicInfo ? (
                    <>
                      <DropdownContainer>
                        <DropdownButton 
                          type="button"
                          onClick={() => handleDropdownToggle('year')}
                          $isOpen={dropdownStates.year}
                          $hasValue={!!formData.birthYear}
                        >
                          <span>{formData.birthYear || '연'}</span>
                          <BirthDropdownChevron $isOpen={dropdownStates.year}>
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                              <path d="M11.07 6.40L8 9.47L4.93 6.40" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </BirthDropdownChevron>
                        </DropdownButton>
                        {dropdownStates.year && (
                          <DropdownMenu>
                            {years.map(year => (
                              <DropdownItem
                                key={year}
                                onClick={() => handleDropdownSelect('year', year.toString())}
                              >
                                {year}
                              </DropdownItem>
                            ))}
                          </DropdownMenu>
                        )}
                      </DropdownContainer>

                      <DropdownContainer>
                        <DropdownButton 
                          type="button"
                          onClick={() => handleDropdownToggle('month')}
                          $isOpen={dropdownStates.month}
                          $hasValue={!!formData.birthMonth}
                        >
                          <span>{formData.birthMonth || '월'}</span>
                          <BirthDropdownChevron $isOpen={dropdownStates.month}>
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                              <path d="M11.07 6.40L8 9.47L4.93 6.40" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </BirthDropdownChevron>
                        </DropdownButton>
                        {dropdownStates.month && (
                          <DropdownMenu>
                            {months.map(month => (
                              <DropdownItem
                                key={month}
                                onClick={() => handleDropdownSelect('month', month.toString())}
                              >
                                {month}
                              </DropdownItem>
                            ))}
                          </DropdownMenu>
                        )}
                      </DropdownContainer>

                      <DropdownContainer>
                        <DropdownButton 
                          type="button"
                          onClick={() => handleDropdownToggle('day')}
                          $isOpen={dropdownStates.day}
                          $hasValue={!!formData.birthDay}
                        >
                          <span>{formData.birthDay || '일'}</span>
                          <BirthDropdownChevron $isOpen={dropdownStates.day}>
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                              <path d="M11.07 6.40L8 9.47L4.93 6.40" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </BirthDropdownChevron>
                        </DropdownButton>
                        {dropdownStates.day && (
                          <DropdownMenu>
                            {days.map(day => (
                              <DropdownItem
                                key={day}
                                onClick={() => handleDropdownSelect('day', day.toString())}
                              >
                                {day}
                              </DropdownItem>
                            ))}
                          </DropdownMenu>
                        )}
                      </DropdownContainer>
                    </>
                  ) : (
                    <>
                      <DisabledBirthInput value={formData.birthYear} readOnly />
                      <DisabledBirthInput value={formData.birthMonth} readOnly />
                      <DisabledBirthInput value={formData.birthDay} readOnly />
                    </>
                  )}
                </BirthContainer>
              </FormGroup>

              {/* 휴대폰 번호 */}
              <FormGroup>
                <FormLabel>휴대폰 번호</FormLabel>
                {loading ? (
                  <SkeletonInput />
                ) : isEditingBasicInfo ? (
                  <FormInput
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                  />
                ) : (
                  <DisabledInput value={formData.phone} readOnly />
                )}
              </FormGroup>
            </Section>

            <Divider />

            {/* 회원 자격 인증 섹션 */}
            <Section>
              <SectionHeader>
                <SectionTitle>회원 자격 인증</SectionTitle>
              </SectionHeader>

              {/* 나의 인증 내역 */}
              <VerificationGroup>
                <VerificationLabel>나의 인증 내역</VerificationLabel>
                <VerificationCard>
                  {!userInfo ? (
                    // 사용자 정보 로딩 중
                    <>
                      <VerificationIcon>
                        <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M17.334 10.6673C17.334 11.4037 16.7371 12.0007 16.0007 12.0007C15.2643 12.0007 14.6674 11.4037 14.6674 10.6673C14.6674 9.93094 15.2643 9.33398 16.0007 9.33398C16.7371 9.33398 17.334 9.93094 17.334 10.6673Z" fill="#37383C" fillOpacity="0.61"/>
                          <path d="M17.2008 15.334C17.2008 14.6712 16.6636 14.134 16.0008 14.134C15.3381 14.134 14.8009 14.6712 14.8009 15.334V21.334C14.8009 21.9967 15.3381 22.534 16.0008 22.534C16.6636 22.534 17.2008 21.9967 17.2008 21.334V15.334Z" fill="#37383C" fillOpacity="0.61"/>
                          <path fillRule="evenodd" clipRule="evenodd" d="M2.80078 16.0008C2.80078 8.71062 8.71059 2.80078 16.0007 2.80078C23.2908 2.80078 29.2007 8.71062 29.2007 16.0008C29.2007 23.2909 23.2908 29.2008 16.0007 29.2008C8.71059 29.2008 2.80078 23.2909 2.80078 16.0008ZM16.0007 5.20078C10.0361 5.20078 5.20077 10.0361 5.20077 16.0008C5.20077 21.9655 10.0361 26.8008 16.0007 26.8008C21.9654 26.8008 26.8007 21.9655 26.8007 16.0008C26.8007 10.0361 21.9654 5.20078 16.0007 5.20078Z" fill="#37383C" fillOpacity="0.61"/>
                        </svg>
                      </VerificationIcon>
                      <VerificationText style={{ color: 'rgba(55, 56, 60, 0.61)' }}>로딩 중...</VerificationText>
                    </>
                  ) : userInfo.approved === 'APPROVED' ? (
                    // 인증 완료 상태
                    <>
                      <VerificationIcon>
                        <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M21.1821 13.8492C21.6507 13.3806 21.6508 12.6208 21.1822 12.1521C20.7135 11.6835 19.9538 11.6835 19.4851 12.1521L14.3334 17.3036L12.5156 15.4855C12.047 15.0169 11.2872 15.0168 10.8185 15.4854C10.3498 15.954 10.3498 16.7138 10.8184 17.1825L13.4847 19.8491C13.7097 20.0742 14.015 20.2006 14.3332 20.2007C14.6515 20.2007 14.9567 20.0742 15.1818 19.8492L21.1821 13.8492Z" fill="#448181"/>
                          <path fillRule="evenodd" clipRule="evenodd" d="M13.3676 3.23687C14.8152 1.76734 17.1856 1.76744 18.6332 3.2369L20.1172 4.74327C20.3632 4.99292 20.6997 5.13228 21.0501 5.12965L23.1645 5.11383C25.2273 5.09838 26.9033 6.7746 26.8879 8.83725L26.8721 10.9517C26.8695 11.3022 27.009 11.6388 27.2585 11.8846L28.7649 13.3686C30.2344 14.8163 30.2343 17.1867 28.7649 18.6343L27.2585 20.1182C27.0089 20.3642 26.8695 20.7008 26.8721 21.0512L26.8879 23.1657C26.9033 25.2283 25.2272 26.9044 23.1645 26.889L21.0501 26.8732C20.6996 26.8706 20.3631 27.01 20.1171 27.2596L18.6332 28.766C17.1856 30.2355 14.8153 30.2355 13.3676 28.766L11.8835 27.2596C11.6377 27.01 11.3011 26.8706 10.9506 26.8732L8.83616 26.889C6.77352 26.9044 5.09738 25.2284 5.11283 23.1656L5.12865 21.0511C5.13128 20.7008 4.99184 20.3642 4.74219 20.1181L3.2359 18.6343C1.76641 17.1866 1.76642 14.8163 3.2359 13.3686L4.74226 11.8846C4.99189 11.6387 5.13128 11.3021 5.12865 10.9517L5.11283 8.83724C5.09738 6.77449 6.77348 5.09838 8.83623 5.11383L10.9507 5.12965C11.3011 5.13228 11.6377 4.99286 11.8836 4.74323L13.3676 3.23687ZM16.9235 4.92118C16.4159 4.40592 15.5848 4.40601 15.0773 4.92114L13.5932 6.42758C12.8919 7.13951 11.9321 7.53707 10.9327 7.52959L8.81825 7.51376C8.09501 7.50835 7.50734 8.09602 7.51275 8.81928L7.52858 10.9338C7.53606 11.9331 7.13848 12.8929 6.42655 13.5943L4.9202 15.0783C4.40496 15.5859 4.40497 16.417 4.9202 16.9246L6.42662 18.4086C7.13849 19.11 7.53606 20.0697 7.52858 21.0691L7.51275 23.1836C7.50734 23.9067 8.09501 24.4944 8.81832 24.4891L10.9329 24.4732C11.9321 24.4658 12.8919 24.8633 13.5933 25.5753L15.0773 27.0817C15.5849 27.5969 16.416 27.5969 16.9235 27.0817L18.4075 25.5753C19.1089 24.8633 20.0688 24.4658 21.068 24.4732L23.1825 24.4891C23.9057 24.4944 24.4933 23.9067 24.488 23.1835L24.4722 21.069C24.4648 20.0697 24.8623 19.1099 25.5743 18.4085L27.0806 16.9246C27.5958 16.417 27.5958 15.5859 27.0806 15.0783L25.5742 13.5943C24.8622 12.8929 24.4648 11.933 24.4722 10.9338L24.488 8.81926C24.4933 8.09595 23.9056 7.50835 23.1825 7.51376L21.068 7.52959C20.0687 7.53707 19.1088 7.13942 18.4074 6.42754L16.9235 4.92118Z" fill="#448181"/>
                        </svg>
                      </VerificationIcon>
                      <VerificationText>
                        {userInfo.identity === 'STUDENT' ? '의대생' : '의사'}
                        {(userInfo.medicine || userInfo.university) && (
                          <> · {userInfo.identity === 'STUDENT' ? userInfo.university : userInfo.medicine}</>
                        )}
                      </VerificationText>
                    </>
                  ) : userInfo.approved === 'PENDING' ? (
                    // 인증 대기 중 상태
                    <>
                      <VerificationIcon>
                        <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M17.334 10.6673C17.334 11.4037 16.7371 12.0007 16.0007 12.0007C15.2643 12.0007 14.6674 11.4037 14.6674 10.6673C14.6674 9.93094 15.2643 9.33398 16.0007 9.33398C16.7371 9.33398 17.334 9.93094 17.334 10.6673Z" fill="#37383C" fillOpacity="0.61"/>
                          <path d="M17.2008 15.334C17.2008 14.6712 16.6636 14.134 16.0008 14.134C15.3381 14.134 14.8009 14.6712 14.8009 15.334V21.334C14.8009 21.9967 15.3381 22.534 16.0008 22.534C16.6636 22.534 17.2008 21.9967 17.2008 21.334V15.334Z" fill="#37383C" fillOpacity="0.61"/>
                          <path fillRule="evenodd" clipRule="evenodd" d="M2.80078 16.0008C2.80078 8.71062 8.71059 2.80078 16.0007 2.80078C23.2908 2.80078 29.2007 8.71062 29.2007 16.0008C29.2007 23.2909 23.2908 29.2008 16.0007 29.2008C8.71059 29.2008 2.80078 23.2909 2.80078 16.0008ZM16.0007 5.20078C10.0361 5.20078 5.20077 10.0361 5.20077 16.0008C5.20077 21.9655 10.0361 26.8008 16.0007 26.8008C21.9654 26.8008 26.8007 21.9655 26.8007 16.0008C26.8007 10.0361 21.9654 5.20078 16.0007 5.20078Z" fill="#37383C" fillOpacity="0.61"/>
                        </svg>
                      </VerificationIcon>
                      <VerificationText style={{ color: 'rgba(55, 56, 60, 0.61)' }}>인증 대기 중</VerificationText>
                    </>
                  ) : (
                    // 인증이 필요한 상태
                    <>
                      <VerificationIcon>
                        <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M17.334 10.6673C17.334 11.4037 16.7371 12.0007 16.0007 12.0007C15.2643 12.0007 14.6674 11.4037 14.6674 10.6673C14.6674 9.93094 15.2643 9.33398 16.0007 9.33398C16.7371 9.33398 17.334 9.93094 17.334 10.6673Z" fill="#37383C" fillOpacity="0.61"/>
                          <path d="M17.2008 15.334C17.2008 14.6712 16.6636 14.134 16.0008 14.134C15.3381 14.134 14.8009 14.6712 14.8009 15.334V21.334C14.8009 21.9967 15.3381 22.534 16.0008 22.534C16.6636 22.534 17.2008 21.9967 17.2008 21.334V15.334Z" fill="#37383C" fillOpacity="0.61"/>
                          <path fillRule="evenodd" clipRule="evenodd" d="M2.80078 16.0008C2.80078 8.71062 8.71059 2.80078 16.0007 2.80078C23.2908 2.80078 29.2007 8.71062 29.2007 16.0008C29.2007 23.2909 23.2908 29.2008 16.0007 29.2008C8.71059 29.2008 2.80078 23.2909 2.80078 16.0008ZM16.0007 5.20078C10.0361 5.20078 5.20077 10.0361 5.20077 16.0008C5.20077 21.9655 10.0361 26.8008 16.0007 26.8008C21.9654 26.8008 26.8007 21.9655 26.8007 16.0008C26.8007 10.0361 21.9654 5.20078 16.0007 5.20078Z" fill="#37383C" fillOpacity="0.61"/>
                        </svg>
                      </VerificationIcon>
                      <VerificationText style={{ color: 'rgba(55, 56, 60, 0.61)' }}>인증이 필요합니다</VerificationText>
                    </>
                  )}
                </VerificationCard>
              </VerificationGroup>

              {/* 추가 인증 */}
              <VerificationGroup>
                <VerificationLabel>
                  추가 인증
                  <VerificationDescription>
                    기존 인증 내역을 수정·변경 혹은 갱신·최신화하실 수 있습니다. 제출해주신 정보를 바탕으로 관리자 확인을 거쳐 추가 인증이 최종 승인됩니다.
                  </VerificationDescription>
                </VerificationLabel>
                
                
                {/* 탭 */}
                <VerificationTabs>
                  <VerificationTab 
                    className={activeVerificationTab === 'student' ? 'active' : ''}
                    onClick={() => handleVerificationTabChange('student')}
                  >
                    의대생
                  </VerificationTab>
                  <VerificationTab 
                    className={activeVerificationTab === 'doctor' ? 'active' : ''}
                    onClick={() => handleVerificationTabChange('doctor')}
                  >
                    의사
                  </VerificationTab>
                </VerificationTabs>

                {/* 입력 필드 */}
                {activeVerificationTab === 'student' ? (
                  <FormGroup>
                    <FormSelect 
                      name="department"
                      value={identityFormData.department} 
                      onChange={handleIdentityInputChange}
                      disabled={isLoadingOptions.universities}
                      $hasValue={!!identityFormData.department && identityFormData.department !== ''}
                    >
                      <option value="">
                        {isLoadingOptions.universities ? '로딩 중...' : '소속 대학을 선택해주세요.'}
                      </option>
                      {universities.map((university) => (
                        <option key={university} value={university}>
                          {university}
                        </option>
                      ))}
                    </FormSelect>
                  </FormGroup>
                ) : (
                  <DoctorFormGroup>
                    <FormGroup>
                      <FormSelect
                        name="department"
                        value={identityFormData.department}
                        onChange={handleIdentityInputChange}
                        disabled={isLoadingOptions.departments}
                        $hasValue={!!identityFormData.department}
                      >
                        <option value="">
                          {isLoadingOptions.departments ? '로딩 중...' : '진료과를 선택해주세요.'}
                        </option>
                        {departments.map((department) => (
                          <option key={department} value={department}>
                            {department}
                          </option>
                        ))}
                      </FormSelect>
                    </FormGroup>
                    
                    <FormGroup>
                      <FormInput
                        type="text"
                        name="licenseNumber"
                        value={identityFormData.licenseNumber}
                        onChange={handleIdentityInputChange}
                        placeholder="면허번호를 입력해주세요."
                      />
                    </FormGroup>
                  </DoctorFormGroup>
                )}

                {/* 이미지 업로드 (의대생일 때만) */}
                {activeVerificationTab === 'student' && (
                  <UploadSection>
                    <UploadDescription>
                      이름, 소속 대학, 학번이 식별 가능한 학생증 혹은 재학증명서 이미지를 업로드해주세요.
                      {identityFormData.selectedFile && (
                        <SelectedFileName>
                          선택된 파일: {identityFormData.selectedFile.name}
                        </SelectedFileName>
                      )}
                    </UploadDescription>
                    <UploadButton onClick={() => identityFileInputRef.current?.click()}>
                      이미지 업로드
                    </UploadButton>
                    <HiddenFileInput
                      ref={identityFileInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/gif,image/webp,application/pdf"
                      onChange={handleIdentityFileChange}
                    />
                  </UploadSection>
                )}

                {/* 제출 버튼 */}
                <SubmitButton 
                  disabled={
                    isSubmittingIdentity ||
                    (activeVerificationTab === 'student' 
                      ? !identityFormData.department || !identityFormData.selectedFile
                      : !identityFormData.department || !identityFormData.licenseNumber)
                  }
                  active={
                    !isSubmittingIdentity &&
                    (activeVerificationTab === 'student' 
                      ? !!(identityFormData.department && identityFormData.selectedFile)
                      : !!(identityFormData.department && identityFormData.licenseNumber))
                  }
                  onClick={handleIdentitySubmit}
                >
                  {isSubmittingIdentity ? '제출 중...' : '제출하기'}
                </SubmitButton>
              </VerificationGroup>
            </Section>

            <Divider />

            {/* 마케팅 수신 동의 섹션 */}
            <Section>
              <SectionHeader>
                <SectionTitle>마케팅 수신 동의</SectionTitle>
                <ToggleSwitch 
                  active={marketingConsent}
                  onClick={handleMarketingConsentToggle}
                >
                  <ToggleSlider active={marketingConsent} />
                </ToggleSwitch>
              </SectionHeader>

              <MarketingText>
                ｢대한민국의학한림원 × 투비닥터 의료 AI 캠퍼스｣의 새로운 기능이나 업데이트 소식, 회원님께 유용한 맞춤 정보 등 다양한 소식을 빠르게 받아보실 수 있습니다.
              </MarketingText>
            </Section>
          </MainContent>
        </MainContainer>
      </Container>
      
      {/* 기본정보 수정 모달 */}
      <BasicInfoEditModal
        isOpen={isBasicInfoModalOpen}
        onClose={() => setIsBasicInfoModalOpen(false)}
        onComplete={handleBasicInfoUpdate}
        initialData={userInfo ? {
          name: userInfo.name || '',
          birth: userInfo.birth || '',
          phone: userInfo.phone || ''
        } : undefined}
      />
    </MyPageLayout>
  );
};// 스타일 컴포넌트들
const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: flex-end;
  align-items: flex-start;
`;

const MainContainer = styled.div`
  width: 100%;
  max-width: 894px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 32px;
`;

const PageHeader = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 16px;
  margin-top: 60px;
`;

const PageTitle = styled.h1`
  color: #171719;
  font-size: 40px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 700;
  line-height: 52px;
  margin: 0;
`;

const NavigationContainer = styled.div`
  width: 100%;
  height: 32px;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 32px;
`;

const MainContent = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 48px;
`;

const Section = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const SectionHeader = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 12px;
`;

const SectionTitle = styled.h2`
  color: black;
  font-size: 28px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 700;
  line-height: 38px;
  margin: 0;
  flex: 1;
`;

const EditButton = styled.button<{ active?: boolean }>`
  padding: 9px 20px;
  border-radius: 10px;
  border: 1px solid rgba(112, 115, 124, 0.16);
  background: transparent;
  color: #448181;
  font-size: 15px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 22px;
  letter-spacing: 0.14px;
  cursor: pointer;
  transition: all 0.2s ease;
  height: 40px;
  
  &:hover {
    opacity: 0.8;
  }
`;

const ProfileImageSection = styled.div`
  position: relative;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 10px;
`;

const ProfileImageContainer = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ProfileImageWrapper = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: white;
  outline: 1px solid rgba(112, 115, 124, 0.08);
  outline-offset: -1px;
  position: relative;
  /* overflow 제거하여 편집 버튼이 밖으로 나올 수 있게 함 */
  transition: opacity 0.2s ease;
  
  &:hover .overlay {
    /* opacity: 1; */
  }
`;

const ProfileImageOverlay = styled.div.attrs({
  className: 'overlay'
})`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.3);
  opacity: 0;
  transition: opacity 0.2s ease;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const UploadSpinner = styled.div`
  width: 12px;
  height: 12px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const ProfileImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
`;

const EditIconButton = styled.button<{ disabled?: boolean }>`
  position: absolute;
  right: -4px;
  bottom: -4px;
  width: 24px;
  height: 24px;
  background: #448181;
  border: none;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.disabled ? 0.6 : 1};
  z-index: 10;
  transition: all 0.2s ease;
  
  &:hover {
    opacity: ${props => props.disabled ? 0.6 : 0.8};
  }
`;

const FormGroup = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FormRow = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-start;
  align-items: flex-end;
  gap: 10px;
`;

const FormLabel = styled.label`
  color: rgba(46, 47, 51, 0.88);
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 20px;
  letter-spacing: 0.2px;
`;

const FormInput = styled.input`
  width: 100%;
  padding: 12px;
  border-radius: 12px;
  border: 1px solid rgba(112, 115, 124, 0.16);
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0.09px;
  color: #171719;
  outline: none;
  box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.03);
  
  &:focus {
    border-color: #448181;
  }
`;

const DisabledInput = styled.input`
  width: 100%;
  padding: 12px;
  border-radius: 12px;
  border: 1px solid rgba(112, 115, 124, 0.08);
  background: #F4F4F5;
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0.09px;
  color: #171719;
  box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.03);
  cursor: not-allowed;
`;

// 생년월일 드롭다운 스타일 (회원가입과 동일)
const BirthContainer = styled.div`
  display: flex;
  gap: 10px;
  align-items: flex-end;
`;

const DropdownContainer = styled.div`
  flex: 1;
  position: relative;
`;

const DropdownButton = styled.button<{ $isOpen?: boolean; $hasValue?: boolean }>`
  width: 100%;
  height: 48px;
  padding: 12px;
  border: ${props => props.$isOpen ? '2px solid #448181' : '1px solid rgba(112, 115, 124, 0.16)'};
  border-radius: 12px;
  background: white;
  box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.03);
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;

  span {
    color: ${props => props.$hasValue ? '#171719' : 'rgba(55, 56, 60, 0.28)'};
    font-size: 16px;
    font-weight: 400;
    line-height: 24px;
  }
`;

const BirthDropdownChevron = styled.div<{ $isOpen?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease;
  transform: ${props => props.$isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
  
  svg {
    color: rgba(55, 56, 60, 0.61);
  }
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid rgba(112, 115, 124, 0.16);
  border-radius: 12px;
  box-shadow: 0px 4px 16px rgba(0, 0, 0, 0.1);
  max-height: 200px;
  overflow-y: auto;
  z-index: 1000;
  margin-top: 4px;
`;

const DropdownItem = styled.div`
  padding: 12px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  color: #171719;
  
  &:hover {
    background-color: rgba(68, 129, 129, 0.05);
  }
  
  &:first-child {
    border-radius: 12px 12px 0 0;
  }
  
  &:last-child {
    border-radius: 0 0 12px 12px;
  }
`;

const DisabledBirthInput = styled.input`
  flex: 1;
  padding: 12px;
  border-radius: 12px;
  border: 1px solid rgba(112, 115, 124, 0.08);
  background: #F4F4F5;
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0.09px;
  color: #171719;
  box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.03);
  cursor: not-allowed;
`;

const ChangeButton = styled.button`
  padding: 12px 28px;
  border-radius: 12px;
  border: 1px solid rgba(112, 115, 124, 0.16);
  background: transparent;
  color: #448181;
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 24px;
  letter-spacing: 0.09px;
  cursor: pointer;
  height: 48px;
  
  &:hover {
    opacity: 0.8;
  }
`;

const PasswordChangeSection = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const PasswordHint = styled.div`
  color: rgba(55, 56, 60, 0.61);
  font-size: 12px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: 0.3px;
`;

const PasswordChangeButton = styled.button<{ active: boolean }>`
  width: 100%;
  padding: 12px 28px;
  border-radius: 12px;
  border: none;
  background: ${props => props.active ? '#448181' : '#F4F4F5'};
  color: ${props => props.active ? 'white' : 'rgba(55, 56, 60, 0.28)'};
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 24px;
  letter-spacing: 0.09px;
  cursor: ${props => props.active ? 'pointer' : 'not-allowed'};
  
  &:hover {
    opacity: ${props => props.active ? 0.8 : 1};
  }
`;

const DoctorVerificationNote = styled.div`
  width: 100%;
  color: rgba(55, 56, 60, 0.61);
  font-size: 13px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 18px;
  letter-spacing: 0.25px;
  text-align: center;
  padding: 0 10px;
`;

const DoctorFormGroup = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background: rgba(112, 115, 124, 0.22);
`;

const VerificationGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const VerificationLabel = styled.div`
  color: rgba(46, 47, 51, 0.88);
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 20px;
  letter-spacing: 0.2px;
  text-align: start;
`;

const VerificationCard = styled.div`
  width: 100%;
  height: 172px;
  padding: 48px 32px;
  border-radius: 12px;
  border: 1px solid rgba(112, 115, 124, 0.16);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 24px;
`;

const VerificationIcon = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const VerificationText = styled.div`
  color: #171719;
  font-size: 20px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 28px;
`;

const VerificationDescription = styled.div`
  color: rgba(55, 56, 60, 0.61);
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0.2px;
  padding-top: 8px;
`;

const VerificationTabs = styled.div`
  display: flex;
  width: 100%;
  margin-bottom: 16px;
  position: relative;
`;

const VerificationTab = styled.button`
  flex: 1;
  padding: 10px 0;
  text-align: center;
  cursor: pointer;
  background-color: white;
  border: 1px solid rgba(112, 115, 124, 0.22);
  font-size: 17px;
  font-weight: 500;
  color: rgba(55, 56, 60, 0.61);
  position: relative;
  z-index: 1;
  
  &:first-child {
    border-radius: 12px 0 0 12px;
  }
  
  &:last-child {
    border-radius: 0 12px 12px 0;
    margin-left: -1px;
  }
  
  &.active {
    border-color: #448181;
    color: #448181;
    background-color: rgba(68, 129, 129, 0.05);
    z-index: 2;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 14px;
  }
`;

const FormSelect = styled.select<{ $hasValue?: boolean }>`
  width: 100%;
  padding: 12px 40px 12px 12px; /* 우측 패딩 증가하여 화살표와 갭 확보 */
  border-radius: 12px;
  border: 1px solid rgba(112, 115, 124, 0.16);
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0.09px;
  color: ${props => props.$hasValue ? '#171719' : 'rgba(55, 56, 60, 0.28)'};
  background: white;
  cursor: pointer;
  box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.03);
  appearance: none; /* 기본 화살표 제거 */
  background-image: url("data:image/svg+xml,%3Csvg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M2.46967 5.46967C2.76256 5.17678 3.23744 5.17678 3.53033 5.46967L8 9.93934L12.4697 5.46967C12.7626 5.17678 13.2374 5.17678 13.5303 5.46967C13.8232 5.76256 13.8232 6.23744 13.5303 6.53033L8.53033 11.5303C8.23744 11.8232 7.76256 11.8232 7.46967 11.5303L2.46967 6.53033C2.17678 6.23744 2.17678 5.76256 2.46967 5.46967Z' fill='rgba(55, 56, 60, 0.61)'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center; /* 오른쪽 12px 위치에 화살표 배치 */
  
  &:focus {
    border-color: #448181;
    outline: none;
  }
  
  /* 선택된 항목의 색상 */
  option {
    color: #171719;
  }
`;

const UploadSection = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
`;

const UploadDescription = styled.div`
  flex: 1;
  color: rgba(55, 56, 60, 0.61);
  font-size: 13px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 18px;
  letter-spacing: 0.25px;
`;

const SelectedFileName = styled.div`
  margin-top: 8px;
  color: #448181;
  font-size: 12px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  line-height: 16px;
`;

const UploadButton = styled.button`
  padding: 12px 28px;
  border-radius: 12px;
  border: 1px solid #448181;
  background: transparent;
  color: #448181;
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 24px;
  letter-spacing: 0.09px;
  cursor: pointer;
  
  &:hover {
    opacity: 0.8;
  }
`;

const SubmitButton = styled.button<{ disabled?: boolean; active?: boolean }>`
  width: 100%;
  padding: 12px 28px;
  border-radius: 12px;
  border: none;
  background: ${props => props.active && !props.disabled ? '#448181' : '#F4F4F5'};
  color: ${props => props.active && !props.disabled ? 'white' : 'rgba(55, 56, 60, 0.28)'};
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 24px;
  letter-spacing: 0.09px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  
  &:hover {
    opacity: ${props => props.disabled ? 1 : 0.8};
  }
`;

const ToggleSwitch = styled.button<{ active: boolean }>`
  width: 52px;
  height: 32px;
  padding: 4px;
  background: ${props => props.active ? '#448181' : 'rgba(112, 115, 124, 0.16)'};
  border: none;
  border-radius: 100px;
  cursor: pointer;
  position: relative;
  transition: background-color 0.2s ease;
  
  &:hover {
    opacity: 0.8;
  }
`;

const ToggleSlider = styled.div<{ active: boolean }>`
  width: 24px;
  height: 24px;
  background: white;
  border-radius: 50%;
  transition: transform 0.2s ease;
  transform: ${props => props.active ? 'translateX(20px)' : 'translateX(0)'};
`;

const MarketingText = styled.div`
  color: rgba(55, 56, 60, 0.61);
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: 0.2px;
`;

// 스켈레톤 애니메이션
const shimmer = `
  @keyframes shimmer {
    0% {
      background-position: -468px 0;
    }
    100% {
      background-position: 468px 0;
    }
  }
`;

const SkeletonBase = styled.div`
  ${shimmer}
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 400% 100%;
  animation: shimmer 1.2s ease-in-out infinite;
  border-radius: 12px;
`;

const SkeletonInput = styled(SkeletonBase)`
  height: 48px;
  width: 100%;
`;

const SkeletonBirthInput = styled(SkeletonBase)`
  height: 48px;
  flex: 1;
`;

const SkeletonButton = styled(SkeletonBase)`
  height: 40px;
  width: 80px;
`;

const SkeletonCircle = styled(SkeletonBase)`
  width: 64px;
  height: 64px;
  border-radius: 50%;
`;

export default MyPageProfileEdit;